import { useDemoMode } from '@/contexts/DemoModeContext'
import {
  demoMembers,
  demoEvents,
  demoAnnouncements,
  demoPrayerRequests,
  demoTestimonies,
  demoContributions,
  demoEvangelismContacts,
  demoEvangelismStats,
  demoAttendanceRecords,
  demoAttendanceTrends,
  demoFasts,
  demoFastHistory,
  demoReadingPlans,
  demoDevotions,
  demoDashboardMetrics,
  demoGivingTrends,
  demoRecentActivities,
  DEMO_CHURCH_NAME,
  DEMO_CURRENCY_SYMBOL,
} from '@/data/demoData'

export function useDemoData() {
  const { isDemoMode } = useDemoMode()

  return {
    isDemoMode,
    // Data
    members: isDemoMode ? demoMembers : null,
    events: isDemoMode ? demoEvents : null,
    announcements: isDemoMode ? demoAnnouncements : null,
    prayerRequests: isDemoMode ? demoPrayerRequests : null,
    testimonies: isDemoMode ? demoTestimonies : null,
    contributions: isDemoMode ? demoContributions : null,
    evangelismContacts: isDemoMode ? demoEvangelismContacts : null,
    evangelismStats: isDemoMode ? demoEvangelismStats : null,
    attendanceRecords: isDemoMode ? demoAttendanceRecords : null,
    attendanceTrends: isDemoMode ? demoAttendanceTrends : null,
    fasts: isDemoMode ? demoFasts : null,
    fastHistory: isDemoMode ? demoFastHistory : null,
    readingPlans: isDemoMode ? demoReadingPlans : null,
    devotions: isDemoMode ? demoDevotions : null,
    dashboardMetrics: isDemoMode ? demoDashboardMetrics : null,
    givingTrends: isDemoMode ? demoGivingTrends : null,
    recentActivities: isDemoMode ? demoRecentActivities : null,
    // Constants
    churchName: isDemoMode ? DEMO_CHURCH_NAME : null,
    currencySymbol: isDemoMode ? DEMO_CURRENCY_SYMBOL : null,
  }
}

// Hook for dashboard-specific demo data
export function useDemoDashboard() {
  const { isDemoMode } = useDemoMode()

  if (!isDemoMode) {
    return { isDemoMode: false, data: null }
  }

  return {
    isDemoMode: true,
    data: {
      metrics: demoDashboardMetrics,
      currencySymbol: DEMO_CURRENCY_SYMBOL,
      recentMembers: demoMembers.slice(0, 4),
      upcomingEvents: demoEvents.slice(0, 3),
      givingData: demoGivingTrends,
      attendanceData: demoAttendanceTrends,
      recentActivities: demoRecentActivities,
    }
  }
}

// Hook for members page demo data
export function useDemoMembers() {
  const { isDemoMode } = useDemoMode()

  if (!isDemoMode) {
    return { isDemoMode: false, data: null }
  }

  return {
    isDemoMode: true,
    data: {
      members: demoMembers,
      totalMembers: demoMembers.length,
      activeMembers: demoMembers.filter(m => m.status === 'Active').length,
      visitors: demoMembers.filter(m => m.status === 'Visitor').length,
      inactiveMembers: demoMembers.filter(m => m.status === 'Inactive').length,
    }
  }
}

// Hook for events page demo data
export function useDemoEvents() {
  const { isDemoMode } = useDemoMode()

  if (!isDemoMode) {
    return { isDemoMode: false, data: null }
  }

  return {
    isDemoMode: true,
    data: {
      events: demoEvents,
    }
  }
}

// Hook for announcements page demo data
export function useDemoAnnouncements() {
  const { isDemoMode } = useDemoMode()

  if (!isDemoMode) {
    return { isDemoMode: false, data: null }
  }

  return {
    isDemoMode: true,
    data: {
      announcements: demoAnnouncements,
    }
  }
}

// Hook for prayer requests page demo data
export function useDemoPrayerRequests() {
  const { isDemoMode } = useDemoMode()

  if (!isDemoMode) {
    return { isDemoMode: false, data: null }
  }

  return {
    isDemoMode: true,
    data: {
      prayerRequests: demoPrayerRequests,
    }
  }
}

// Hook for evangelism page demo data
export function useDemoEvangelism() {
  const { isDemoMode } = useDemoMode()

  if (!isDemoMode) {
    return { isDemoMode: false, data: null }
  }

  return {
    isDemoMode: true,
    data: {
      contacts: demoEvangelismContacts,
      stats: demoEvangelismStats,
    }
  }
}

// Hook for contributions/giving page demo data
export function useDemoContributions() {
  const { isDemoMode } = useDemoMode()

  if (!isDemoMode) {
    return { isDemoMode: false, data: null }
  }

  const totalGiving = demoContributions.reduce((sum, c) => sum + c.amount, 0)

  return {
    isDemoMode: true,
    data: {
      contributions: demoContributions,
      totalGiving,
      currencySymbol: DEMO_CURRENCY_SYMBOL,
      givingTrends: demoGivingTrends,
    }
  }
}

export default useDemoData
